/**
 * Authentication and Authorization Types
 */

export enum UserRole {
  Admin = 'Admin',
  User = 'User',
  Viewer = 'Viewer',
}

export interface User {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  roles: UserRole[];
  createdAt: Date;
  lastSignIn: Date;
}

export interface AuthState {
  user: User | null;
  roles: UserRole[];
  loading: boolean;
  error: string | null;
  /** Error surfaced from a returning signInWithRedirect leg (see useRedirectResult). */
  redirectError: string | null;
  /** 'local' while the app is used without an account, kept on the phone alone; 'account' when
   * signed in; null until the start has decided (docs/plans/local-mode.md). */
  mode: 'local' | 'account' | null;
  /** The last sign-in would have created an account without a code, and was turned away. */
  accountRefused: boolean;
  /** What was made in local mode on this phone, offered to the account just signed into; null when
   * there's nothing to offer or the user has answered. Never added without being asked. */
  localDataOffer: { households: number; chores: number } | null;
}

export interface SignUpCredentials {
  email: string;
  password: string;
  displayName?: string;
}

export interface SignInCredentials {
  email: string;
  password: string;
}

export type OAuthProvider = 'google' | 'github';

export interface AuthContextValue extends AuthState {
  signUp: (credentials: SignUpCredentials) => Promise<void>;
  signIn: (credentials: SignInCredentials) => Promise<void>;
  signInWithOAuth: (provider: OAuthProvider) => Promise<void>;
  signOut: () => Promise<void>;
  hasRole: (requiredRole: UserRole | UserRole[]) => boolean;
}
